import React, {MutableRefObject, useCallback, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'

import {InternalLinkOption} from "./option"
import Popover from "../../../gd-popover/popover-v2"
import PopoverPosition from "../../../gd-popover/popover-position"
import {PopoverAttachmentType} from "../../../gd-popover/@const"
import SlashDefaultMenu from "./components/default/default-content"
import SlashSearchMenu from "./components/search/search-content"
import {mergeRegister} from "@lexical/utils"
import {COMMAND_PRIORITY_NORMAL, KEY_ARROW_DOWN_COMMAND, KEY_ARROW_UP_COMMAND} from "lexical"
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {useForceUpdate} from "../../../../gd/utils/dom-and-react/react-utils"
import AiChatModule from "../../../../gd-modules/ai-chat/ai-chat"
import {WriteWithAiChat} from "../../../../gd-modules/ai-chat/write-with-ai/write-with-ai-chat"
import {lexicalGetSelectedText} from "../../actions/get-selected-text"
import AIChatContext from "../../../../model2/ai/chat/context/context"
import {AIChatContextType} from "../../../../model2/ai/@const"
import {EditorAIOptions} from "../../editor"
import {BUS_SLASH_MENU_SHOW_CHAT_WITH_AI} from "./@const"

// const debug = "SlashMenu"
const debug = false

export function SlashMenu (
    anchorElementRef    : MutableRefObject<HTMLElement | null>,
    itemProps           : {
        selectedIndex           : number | null
        selectOptionAndCleanUp  : (option: InternalLinkOption) => void
        setHighlightedIndex     : (index: number) => void
        options                 : Array<InternalLinkOption>
    },
    matchingString      : string | null,
    aiOptions?          : EditorAIOptions
) {
    const [editor] = useLexicalComposerContext()

    const forceUpdate = useForceUpdate()

    const [isHidden, setIsHidden] = useState(false)
    const [isChatWithAiSelected, setIsChatWithAiSelected] = useState(false)

    useEffect(() => {
        debug && console.log(debug, 'useEffect anchorElementRef.current', anchorElementRef.current)
        setIsHidden(false)
    }, [anchorElementRef]);

    // Toggles the "chat with AI" state. This will open/close the AI chat panel in the Slash menu.
    const toggleChatWithAI = () => {
        setIsChatWithAiSelected(!isChatWithAiSelected)
    }

    // Subscribe to app-wide bus event "show-chat-with-ai".
    // When the event is triggered, toggle the AI chat panel for the menu.
    useEffect(() => {
        return gd.bus.subscribeNoContext(BUS_SLASH_MENU_SHOW_CHAT_WITH_AI, toggleChatWithAI)
    }, [])

    // When the menu's matchingString changes (i.e., user types a new slash command),
    // automatically close the AI chat panel if it was open.
    useEffect(() => {
        if (isChatWithAiSelected) {
            setIsChatWithAiSelected(false)
        }
    }, [matchingString])

    useEffect(() => {
        let observer: MutationObserver

        // anchorElementRef.current is updated by LexicalTypeaheadMenuPlugin in unknown moment of time
        // in order to get latest node position we should listen to anchorElementRef.current mutation to register when
        // its style is changed and update popover position accordingly
        if (anchorElementRef.current) {
            observer = new MutationObserver(mutations => {
                debug && console.log(debug, 'MutationObserver', mutations)
                forceUpdate()
            })
            observer.observe(anchorElementRef.current, {
                attributes          : true,
                attributeFilter     : ['style'],
                attributeOldValue   : true,
            })
        }

        return ()=> {
            observer?.disconnect()
        }
    }, [anchorElementRef.current])

    debug && console.log(debug, 'render', matchingString)

    useEffect(() => {
        const unregister = mergeRegister(
            editor.registerCommand(KEY_ARROW_DOWN_COMMAND, payload => {
                let newIndex    = itemProps.selectedIndex ? itemProps.selectedIndex : 0
                let option      = undefined
                const length    = itemProps.options.length
                const event     = payload

                while(!option?.id && length > newIndex) {
                    newIndex    = newIndex + 1
                    option      = itemProps.options[newIndex]
                }

                if (!option?.id) {
                    newIndex = _.findLastIndex(itemProps.options, o=>!!o?.id)
                }

                debug && console.log(debug, 'KEY_ARROW_DOWN_COMMAND', newIndex, option, itemProps.selectedIndex)

                itemProps.setHighlightedIndex(newIndex)

                event.preventDefault()
                event.stopImmediatePropagation()

                return true
            }, COMMAND_PRIORITY_NORMAL),
            editor.registerCommand(KEY_ARROW_UP_COMMAND, payload => {
                let newIndex    = itemProps.selectedIndex ? itemProps.selectedIndex : 0
                let option      = undefined
                const event     = payload

                while(!option?.id && newIndex > 0) {
                    newIndex =  newIndex - 1
                    option = itemProps.options[newIndex]
                }

                if (!option?.id) {
                    newIndex = _.findIndex(itemProps.options, o=>!!o?.id)
                }

                debug && console.log(debug, 'KEY_ARROW_UP_COMMAND', newIndex, option, itemProps.selectedIndex)

                itemProps.setHighlightedIndex(newIndex)

                event.preventDefault()
                event.stopImmediatePropagation()

                return true
            }, COMMAND_PRIORITY_NORMAL)
        )

        return () => {
            unregister()
        }
    }, [itemProps.selectedIndex])

    const onClick = useCallback((index:number, option:InternalLinkOption) => {
        debug && console.log(debug, 'onClick', index, option)

        itemProps.setHighlightedIndex(index)
        itemProps.selectOptionAndCleanUp(option)
    }, [itemProps])

    const onPopoverHide = useCallback(()=>{
        if (anchorElementRef.current) {
            gd.utils.Dom.removeClass(anchorElementRef.current,"visible")
        }
    }, [])

    /*const onClickOutside = useCallback((e:any)=>{
        debug && console.log(debug, 'onClickOutside', e)
        if (!showWriteWithAi) {
            return
        }
        setIsHidden(true)
    }, [])*/

    if (anchorElementRef.current) {
        gd.utils.Dom.addClass(anchorElementRef.current,"visible")
    }

    const selectedText: string = lexicalGetSelectedText(editor)

    // return should be after all hooks, otherwise error occurs on rerender ("Rendered fewer hooks than expected")
    if (!anchorElementRef.current || !itemProps.options.length || isHidden) {
        return null
    }

    const showWriteWithAi   = gd.company.acl.canWriteWithAI && isChatWithAiSelected //&& !!matchingString && matchingString.includes("ai")
    const showDefaultMenu   = !matchingString && !showWriteWithAi
    const showSearchMenu    = !!matchingString && !showWriteWithAi

    debug && console.log(debug, 'render', itemProps.selectedIndex)

    const popoverPosition = new PopoverPosition({
        attachment      : "top center",
        targetAttachment: "bottom center",
        attachmentType  : PopoverAttachmentType.HORIZONTAL,
    })

    return createPortal(
        <Popover
            position            = {popoverPosition}
            onPopoverHide       = {onPopoverHide}
            // onClickOutside      = {onClickOutside}
            getTargetFunc       = {()=>anchorElementRef.current?.childNodes[0]}
        >
            {showDefaultMenu &&
                <SlashDefaultMenu
                    options         = {itemProps.options}
                    selectedIndex   = {itemProps.selectedIndex}
                    onClick         = {onClick}
                />}

            {showSearchMenu &&
                <SlashSearchMenu
                    options         = {itemProps.options}
                    selectedIndex   = {itemProps.selectedIndex}
                    onClick         = {onClick}
                />}

            {showWriteWithAi &&
                <WriteWithAiChat
                    className       = "w-375 h-350"
                    editor          = {editor}
                    selectedText    = {selectedText}
                    aiOptions       = {aiOptions}
                />}
        </Popover>,
        anchorElementRef.current,
    )
}
